import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Cake, Trophy, Crown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Browsable monthly view of birthdays + work anniversaries. Reads from
// `/celebrations/month` which the backend computes from crew birthday + hire_date.
// Originally lived in Settings; promoted to the Team page so everyone can see it.
export default function CelebrationsCard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/celebrations/month?year=${year}&month=${month}`);
      setEvents(r.data.events || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const prev = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else { setMonth(month - 1); }
  };
  const next = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else { setMonth(month + 1); }
  };

  const bdays = events.filter((e) => e.type === "birthday");
  const annivs = events.filter((e) => e.type === "anniversary");
  const motm = events.find((e) => e.type === "motm");

  return (
    <div className="dashboard-card" data-testid="celebrations-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cake weight="bold" className="w-5 h-5 text-pink-600" />
          <h3 className="text-lg font-bold" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>Celebrations</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prev} className="px-2 py-1 text-xs rounded hover:bg-slate-100" data-testid="celebrations-prev">‹ Prev</button>
          <span className="text-xs font-semibold px-2" data-testid="celebrations-month-label">{MONTH_NAMES[month - 1]} {year}</span>
          <button onClick={next} className="px-2 py-1 text-xs rounded hover:bg-slate-100" data-testid="celebrations-next">Next ›</button>
        </div>
      </div>

      {events.length === 0 ? (
        <p className="text-xs text-slate-400 py-6 text-center" data-testid="celebrations-empty">Nothing to celebrate this month yet.</p>
      ) : (
        <>
          {motm && (
            <div
              className="mb-3 rounded-2xl p-3 flex items-center gap-3 text-white shadow-md"
              style={{ background: "linear-gradient(135deg,#1d4ed8 0%,#7c3aed 55%,#db2777 100%)" }}
              data-testid="celeb-motm"
            >
              {motm.photo_url ? (
                <img src={motm.photo_url} alt={motm.first_name} className="w-12 h-12 rounded-full object-cover border-2 border-white/70 flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-black bg-amber-400 text-white border-2 border-white/70 flex-shrink-0">
                  {(motm.first_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/80 flex items-center gap-1"><Crown weight="fill" className="w-3.5 h-3.5" /> Member of the Month</p>
                <p className="font-black leading-tight truncate" style={{ fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>{motm.first_name}</p>
                {motm.comments ? (
                  <p className="text-[11px] text-white/85 leading-snug line-clamp-2">“{motm.comments}”</p>
                ) : (
                  <p className="text-[11px] text-white/75">{motm.recognition_count} High Fives</p>
                )}
              </div>
            </div>
          )}
          {(bdays.length > 0 || annivs.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {bdays.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-pink-600 mb-2 flex items-center gap-1"><Cake weight="bold" className="w-3 h-3" /> Birthdays ({bdays.length})</p>
              <ul className="space-y-1.5">
                {bdays.map((e, i) => (
                  <li key={`b-${i}`} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${e.is_today ? "bg-pink-50 ring-1 ring-pink-200" : ""}`} data-testid={`celeb-birthday-${i}`}>
                    {e.photo_url ? (
                      <img src={e.photo_url} alt={e.first_name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: "#DB2777", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                        {(e.first_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium flex-1">{e.first_name}</span>
                    <span className="text-xs text-slate-500">{MONTH_NAMES[month - 1].slice(0, 3)} {parseInt(e.day)}</span>
                    {e.is_today && <span className="text-[9px] font-bold uppercase tracking-wider bg-pink-600 text-white px-1.5 py-0.5 rounded">Today</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {annivs.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1"><Trophy weight="bold" className="w-3 h-3" /> Anniversaries ({annivs.length})</p>
              <ul className="space-y-1.5">
                {annivs.map((e, i) => (
                  <li key={`a-${i}`} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${e.is_today ? "bg-amber-50 ring-1 ring-amber-200" : ""}`} data-testid={`celeb-anniversary-${i}`}>
                    {e.photo_url ? (
                      <img src={e.photo_url} alt={e.first_name} className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: "#F59E0B", fontFamily: "'Five Below Gothic', Chivo, sans-serif" }}>
                        {(e.first_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm font-medium flex-1">{e.first_name}</span>
                    <span className="text-xs text-slate-500">{e.years} yr · {MONTH_NAMES[month - 1].slice(0, 3)} {parseInt(e.day)}</span>
                    {e.is_today && <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-600 text-white px-1.5 py-0.5 rounded">Today</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          </div>
          )}
        </>
      )}
      <p className="text-[11px] text-slate-400 mt-3">Missing dates? Ask the crew member to update their profile.</p>
    </div>
  );
}
